import { useLang } from "@/i18n/LanguageContext";
import { LANGS, LANG_LABEL, LANG_FULL, type Lang } from "@/i18n/translations";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, ChevronDown } from "lucide-react";

const LANG_FLAG: Record<Lang, string> = {
  en: "🇬🇧",
  es: "🇪🇸",
  pt: "🇧🇷",
};

interface Props {
  className?: string;
  /** "compact" shows EN, "full" shows English. */
  variant?: "compact" | "full";
  align?: "start" | "center" | "end";
  /** Show the flag in the trigger button. Defaults to true. */
  showFlagInTrigger?: boolean;
  /** Override hover background/text colors on the trigger. */
  triggerHoverClassName?: string;
  /** Override the dropdown content styling (e.g. for dark header). */
  contentClassName?: string;
  /** Override per-item styling (e.g. for dark header). */
  itemClassName?: string;
}

const LanguageSwitcher = ({
  className,
  variant = "compact",
  align = "end",
  showFlagInTrigger = true,
  triggerHoverClassName = "hover:bg-secondary hover:text-foreground",
  contentClassName,
  itemClassName,
}: Props) => {
  const { lang, switchTo } = useLang();

  const triggerLabel = variant === "compact" ? LANG_LABEL[lang] : LANG_FULL[lang];

  return (
    <DropdownMenu>
      {/* aria-label includes the visible text so screen readers announce both
          the action ("Select language") and current value (e.g. "EN"), and so
          axe's label-content-name-mismatch check passes. */}
      <DropdownMenuTrigger
        aria-label={`Select language, current: ${LANG_FULL[lang]}`}
        className={cn(
          "group inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-sm",
          triggerHoverClassName,
          className
        )}
      >
        {showFlagInTrigger && (
          <span aria-hidden="true" className="text-sm leading-none">{LANG_FLAG[lang]}</span>
        )}
        <span>{triggerLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70 transition-transform duration-200 group-hover:opacity-100 group-data-[state=open]:rotate-180" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={10}
        collisionPadding={16}
        className={cn("min-w-[11rem] rounded-xl border-white/10 p-1.5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]", contentClassName)}
      >
        {LANGS.map((l: Lang) => {
          const active = l === lang;
          return (
            <DropdownMenuItem
              key={l}
              onSelect={() => !active && switchTo(l)}
              className={cn(
                "flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm",
                active && "font-semibold",
                itemClassName,
              )}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden="true" className="text-base leading-none">{LANG_FLAG[l]}</span>
                <span>{LANG_FULL[l]}</span>
              </span>
              {active && <Check className="h-4 w-4 opacity-70" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
